import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';

/**
 * URLs that name one organisation's record, paired with the listing to fall back to.
 *
 * Switching organisation under one of these would leave the new organisation holding the
 * previous one's id, so the switch leaves the URL first. Listing and index URLs name no
 * record and are left alone - they re-scope themselves and the user keeps their screen.
 */
const ORGANIZATION_SCOPED_ROUTES = [
  { pattern: /^\/organization\/clients\/[^/]+/, listRoute: '/organization/clients' },
  {
    // Anything but the `history` segment under the extractor is a workflow id.
    pattern: /^\/extractors\/capital-gains-v2\/(?!history(?:\/|$))[^/]+/,
    listRoute: '/extractors/capital-gains-v2',
  },
] as const;

export function organizationScopedListRoute(pathname: string): string | null {
  return ORGANIZATION_SCOPED_ROUTES.find((route) => route.pattern.test(pathname))?.listRoute ?? null;
}

/**
 * Switch organisation, leaving any record-scoped URL before the organisation changes.
 *
 * The switch is deferred rather than fired alongside the navigation because the route
 * change has to be the earlier of the two: a page that reads the id from the URL would
 * otherwise request the previous organisation's record from the new one.
 */
export function useDeferredOrganizationSwitch(): (organizationId: string) => void {
  const { setSelectedOrganizationId } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingOrganizationId, setPendingOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingOrganizationId === null || organizationScopedListRoute(location.pathname) !== null) {
      return;
    }
    setSelectedOrganizationId(pendingOrganizationId);
    setPendingOrganizationId(null);
  }, [location.pathname, pendingOrganizationId, setSelectedOrganizationId]);

  return (organizationId: string) => {
    const listRoute = organizationScopedListRoute(location.pathname);
    if (listRoute === null) {
      setSelectedOrganizationId(organizationId);
      return;
    }
    setPendingOrganizationId(organizationId);
    void navigate(listRoute);
  };
}
